(ns yardwerkz.web.jobs.chat
  "Job chat component with realtime messages, date separators, and read receipts."
  (:require
   [uix.core :refer [defui $ use-state use-effect use-ref]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.realtime :as realtime]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]))

;; =============================================================================
;; Helpers
;; =============================================================================

(defn- format-time [iso-str]
  (when iso-str
    (try
      (let [d (js/Date. iso-str)
            now (js/Date.)
            diff-ms (- (.getTime now) (.getTime d))
            diff-min (js/Math.floor (/ diff-ms 60000))
            diff-hr (js/Math.floor (/ diff-min 60))]
        (cond
          (< diff-min 1) "Now"
          (< diff-min 60) (str diff-min "m ago")
          (< diff-hr 24) (str diff-hr "h ago")
          :else (let [month (inc (.getMonth d))
                      day (.getDate d)]
                  (str month "/" day))))
      (catch :default _ ""))))

(defn- format-date-separator [iso-str]
  (when iso-str
    (try
      (let [d (js/Date. iso-str)
            now (js/Date.)
            diff-days (js/Math.floor (/ (- now d) 86400000))]
        (cond
          (zero? diff-days) "Today"
          (= diff-days 1) "Yesterday"
          :else (.toLocaleDateString d)))
      (catch :default _ ""))))

(defn- same-day? [iso-a iso-b]
  (when (and iso-a iso-b)
    (let [a (js/Date. iso-a)
          b (js/Date. iso-b)]
      (and (= (.getFullYear a) (.getFullYear b))
           (= (.getMonth a) (.getMonth b))
           (= (.getDate a) (.getDate b))))))

;; =============================================================================
;; Message Bubble
;; =============================================================================

(defui message-bubble [{:keys [msg is-mine is-system show-date? show-read?]}]
  (let [content (or (:content msg) (:message/text msg) "")
        created-at (or (:created-at msg) (:createdAt msg))]
    ($ :div
       ;; Date separator
       (when show-date?
         ($ :div {:class "flex justify-center my-3"}
            ($ :span {:class "text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full"}
               (format-date-separator created-at))))

       (cond
         ;; System message
         is-system
         ($ :div {:class "flex justify-center my-2"}
            ($ :span {:class "text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full"}
               content))

         ;; Regular message
         :else
         ($ :div {:class (str "flex mb-3 " (if is-mine "justify-end" "justify-start"))}
            ($ :div
               ($ :div {:class (str "max-w-xs lg:max-w-md px-4 py-2.5 text-sm "
                                    (if is-mine
                                      "bg-green-600 text-white rounded-2xl rounded-br-sm"
                                      "bg-white text-gray-900 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100"))}
                  content)
               ;; Time + read receipt
               ($ :div {:class (str "mt-1 text-xs "
                                    (if is-mine "text-right" "text-left"))}
                  ($ :span {:class "text-gray-400"} (format-time created-at))
                  (when show-read?
                    ($ :span {:class "text-green-600 ml-1"} "Read")))))))))

;; =============================================================================
;; Chat Panel
;; =============================================================================

(defui chat-panel [{:keys [job-id]}]
  (let [{:keys [data loading?]} (realtime/use-job-messages job-id)
        [text set-text] (use-state "")
        dispatch (rfx/use-dispatch)
        messages-ref (use-ref nil)
        identity (rfx/use-sub [::store/identity])
        my-id (:id identity)

        ;; Sort messages chronologically (oldest first for natural scroll)
        sorted-msgs (vec (sort-by #(or (:created-at %) (:createdAt %)) (or data [])))

        ;; Find last read customer message for receipt
        last-read-sent-id (->> sorted-msgs
                               reverse
                               (filter #(and (= "customer" (:sender-role %))
                                             (:read-at %)))
                               first
                               (#(or (:id %) (:message-id %))))]

    ;; Auto-scroll to bottom on new messages
    (use-effect
     (fn []
       (when-let [el @messages-ref]
         (set! (.-scrollTop el) (.-scrollHeight el)))
       js/undefined)
     [data])

    ($ :div {:class "flex flex-col h-full"}
       ;; Messages
       ($ :div {:ref messages-ref
                :class "flex-1 overflow-y-auto p-4 bg-gray-50"}
          (if loading?
            ($ :div {:class "flex justify-center py-8"}
               ($ ui/spinner {:size :sm}))
            (if (seq sorted-msgs)
              (for [[idx msg] (map-indexed vector sorted-msgs)]
                (let [msg-id (or (:id msg) (:message-id msg))
                      sender-role (or (:sender-role msg) "")
                      is-mine (or (= sender-role "customer")
                                  (= (:message/sender-id msg) my-id))
                      is-system (= sender-role "system")
                      created-at (or (:created-at msg) (:createdAt msg))
                      prev-msg (when (pos? idx) (nth sorted-msgs (dec idx)))
                      prev-created (when prev-msg (or (:created-at prev-msg) (:createdAt prev-msg)))
                      show-date? (or (zero? idx) (not (same-day? created-at prev-created)))]
                  ($ message-bubble {:key (or msg-id idx)
                                     :msg msg
                                     :is-mine is-mine
                                     :is-system is-system
                                     :show-date? show-date?
                                     :show-read? (and is-mine
                                                      (:read-at msg)
                                                      (= msg-id last-read-sent-id))})))
              ($ :div {:class "flex flex-col items-center justify-center h-full text-gray-400"}
                 ($ icons/MessageSquare {:size 32 :class "mb-2"})
                 ($ :p {:class "text-sm"} "No messages yet. Start the conversation!")))))

       ;; Input
       ($ :div {:class "border-t border-gray-200 p-3 bg-white"}
          ($ :form {:class "flex items-center space-x-2"
                    :on-submit (fn [e]
                                 (.preventDefault e)
                                 (when (seq text)
                                   (dispatch [::store/send-message {:job-id job-id :text text}])
                                   (set-text "")))}
             ($ :input {:class "flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50"
                        :value text
                        :on-change #(set-text (.. % -target -value))
                        :placeholder "Type a message..."})
             ($ :button {:type "submit"
                         :class (str "p-2.5 rounded-full transition-colors "
                                     (if (seq text)
                                       "bg-green-600 text-white hover:bg-green-700"
                                       "bg-gray-100 text-gray-400"))
                         :disabled (empty? text)}
                ($ icons/Send {:size 18})))))))
